/**
 * `@rynora/flow-templates-curated` — the curated "showcase" tier of
 * the Templates marketplace.
 *
 * Static, build-time-bundled, Rynora-team-authored flow templates
 * surfaced under the `Templates → Marketplace` tab in the editor and
 * on the landing-page `/marketplace` route. Distinct from the per-org
 * user-saved + AI-generated rows in the `flow_templates` DB table —
 * those are mutable per-org content; this package is read-only
 * Rynora-curated content.
 *
 * Adding a template:
 *   1. Author a new JSON in `src/templates/<persona>.json` matching
 *      `MarketplaceTemplateSchema`.
 *   2. Add it to the `MarketplacePersonaSchema` enum in
 *      `packages/contracts/src/marketplace.ts` (locked decision #6
 *      caps v0 at 5; cap raises to 10 with a tracker entry).
 *   3. Add a row to `CURATED_TEMPLATES` below.
 *   4. Verify `pnpm test --workspace=@rynora/flow-templates-curated` passes.
 *
 * Locked decisions are documented in
 * `docs/handbook/features/pending/templates-marketplace.md` (cleared
 * to `completed/` on this ship).
 */

import { MarketplaceTemplateSchema, type MarketplaceTemplate, type MarketplacePersona } from '@rynora/contracts';

import loginRaw from './templates/login.json';
import checkoutRaw from './templates/checkout.json';
import searchAndFilterRaw from './templates/search-and-filter.json';
import signupRaw from './templates/signup-with-email-verification.json';
import contactFormRaw from './templates/contact-form.json';

/**
 * Parse a raw JSON import through the Zod schema. We do this once
 * at module-load time so any malformed JSON fails fast at the API's
 * boot rather than at the first marketplace request. Throws — the
 * API process should crash on a malformed bundle, since the only
 * recovery is to ship a fix.
 */
function parseOrThrow(raw: unknown, source: string): MarketplaceTemplate {
  const parsed = MarketplaceTemplateSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `[@rynora/flow-templates-curated] ${source} failed schema validation: ${JSON.stringify(parsed.error.issues)}`,
    );
  }
  return parsed.data;
}

/**
 * The full curated catalogue. Order matches the locked-decision #6
 * persona order so the marketplace card grid renders predictably.
 */
export const CURATED_TEMPLATES: readonly MarketplaceTemplate[] = Object.freeze([
  parseOrThrow(loginRaw, 'login.json'),
  parseOrThrow(checkoutRaw, 'checkout.json'),
  parseOrThrow(searchAndFilterRaw, 'search-and-filter.json'),
  parseOrThrow(signupRaw, 'signup-with-email-verification.json'),
  parseOrThrow(contactFormRaw, 'contact-form.json'),
]);

/** Public list helper. Returned array is frozen — callers must not mutate. */
export function listCuratedTemplates(): readonly MarketplaceTemplate[] {
  return CURATED_TEMPLATES;
}

/**
 * Lookup by stable id (kebab-case slug; matches the JSON filename).
 * Returns `null` (not undefined) so a 404 path is unambiguous in the
 * API service.
 */
export function getCuratedTemplate(id: string): MarketplaceTemplate | null {
  return CURATED_TEMPLATES.find((t) => t.id === id) ?? null;
}

/** Lookup by persona — useful for the landing-page deep-link variants. */
export function getCuratedTemplateForPersona(persona: MarketplacePersona): MarketplaceTemplate | null {
  return CURATED_TEMPLATES.find((t) => t.persona === persona) ?? null;
}
